import { Box, Divider } from '@material-ui/core';
import {
  SCheckbox,
  SETVIImage,
  SHighlightText,
  SText
} from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';
import { ImagePreview, ProductStatusBadge } from 'componentsV2';
import { getProductStatus } from 'componentsV2/product/status';
import { getStockInfo } from 'pages/products/components/products-list/useProductColumns/contents/stock-info';
import { ModifiableProduct } from 'pages/sales/modules/orders/subpages/orders/subpages/order-create';

const PLACEHOLDER = '/images/product-placeholder.svg';

interface ProductNameCellProps {
  product: ModifiableProduct;
  searchQuery?: string;
  checked?: boolean;
  /** Omit to render the cell without a checkbox. */
  onCheckedChange?: (checked: boolean) => void;
}

export const ProductNameCell = ({
  product,
  searchQuery,
  checked = false,
  onCheckedChange
}: ProductNameCellProps) => {
  const vendorName =
    (product?.vendor as any)?.name || (product?.vendor as any)?.Name || 'N/A';
  const mfgSku = product?.manufacturerSku
    ? ` (SKU: ${product.manufacturerSku})`
    : '';
  const src = product?.imageUrl || PLACEHOLDER;

  return (
    <Box display="flex" alignItems="center" gridGap={12}>
      {!!onCheckedChange && (
        // Stops the row's click handler (which opens product details) from
        // firing when the user is only ticking the row.
        <Box
          display="flex"
          alignItems="center"
          onClick={e => e.stopPropagation()}>
          <SCheckbox
            checked={checked}
            onChange={(_, isChecked) => onCheckedChange(isChecked)}
            inputProps={{
              'aria-label': `Select ${product?.name || 'product'}`
            }}
          />
        </Box>
      )}
      {/* Same hover preview the catalog's product cell uses — the thumbnail is
          too small to judge a product by. Tooltip needs a child that holds a
          ref, hence the Box. */}
      <ImagePreview
        placement="right"
        arrow
        title={
          <img
            src={src}
            alt={product?.name || 'Product'}
            onError={e => {
              e.currentTarget.src = PLACEHOLDER;
            }}
            style={{
              width: 180,
              height: 180,
              objectFit: 'contain',
              backgroundColor: '#FFFFFF',
              borderRadius: 12,
              padding: 20
            }}
          />
        }>
        <Box display="flex" alignItems="center" style={{ flexShrink: 0 }}>
          <SETVIImage
            src={src}
            errorImage={PLACEHOLDER}
            alt={product?.name || 'Product'}
            // Spans all three text lines (name, SKU/MFG, badges).
            width={72}
            height={72}
            radius={8}
            fit="contain"
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #AFBAC6',
              flexShrink: 0
            }}
          />
        </Box>
      </ImagePreview>
      <Box display="flex" flexDirection="column" minWidth={0}>
        <SHighlightText
          value={toTitleCase(product?.name || '-')}
          weight="semibold"
          searchQuery={searchQuery}
          maxLines={2}
        />
        {/* SKU · MFG · MOQ. Wraps rather than overflowing a narrow column. */}
        <Box display="flex" alignItems="center" gridGap={8} flexWrap="wrap">
          <SHighlightText
            size="sm"
            fontColor={TextColor.SlateGrey}
            value={`SKU: ${product?.sku || '-'}`}
            searchQuery={searchQuery}
          />
          <Divider
            orientation="vertical"
            flexItem
            style={{ height: 12, alignSelf: 'center' }}
          />
          <SHighlightText
            size="sm"
            fontColor={TextColor.SlateGrey}
            value={`MFG: ${vendorName}${mfgSku}`}
            searchQuery={searchQuery}
          />
          {!!product?.unitOfMeasure && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                style={{ height: 12, alignSelf: 'center' }}
              />
              <SHighlightText
                size="sm"
                fontColor={TextColor.SlateGrey}
                value={`UOM: ${product.unitOfMeasure}`}
                searchQuery={searchQuery}
              />
            </>
          )}
          {product?.minimumQuoteQuantity > 1 && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                style={{ height: 12, alignSelf: 'center' }}
              />
              <SHighlightText
                size="sm"
                fontColor={TextColor.SlateGrey}
                value={`MOQ: ${product.minimumQuoteQuantity}`}
                searchQuery={searchQuery}
              />
            </>
          )}
        </Box>
        {/* Same pair the catalog row shows (`componentsV2/product/name`): the
            product's status, then where it's stocked. The stocked badge only
            appears once the response carries `stockInfo`. */}
        <Box
          display="flex"
          alignItems="center"
          gridGap={6}
          flexWrap="wrap"
          mt="2px">
          <ProductStatusBadge>
            <SText size="xs" title={getProductStatus(product)} />
          </ProductStatusBadge>
          {!!product?.stockInfo && (
            <ProductStatusBadge>
              <SText size="xs" title={getStockInfo(product.stockInfo)} />
            </ProductStatusBadge>
          )}
        </Box>
      </Box>
    </Box>
  );
};
